import { useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SettingsSkeleton from "./SettingsSkeleton";

import GradeStatus from "./Grade-Status/SettingsGradeStatus";
import LeadFeatures from "./Lead-Features/LeadFeatures";
import PipelineStatus from "./Pipeline-Status/SettingsPipelineStatus";
import PipelineFeatures from "./Pipeline-Features/PipelineFeatures";
import SettingsSLA from "./SLA/SettingsSLA";
import SettingsIntegrations from "./Integrations/SettingsIntegrations";
import SettingsTheme from "./Theme/SettingsTheme";

import { useSettings } from "../../context/SettingsContext";
import Services from "./Services/Services";
import BookingSystems from "./BookingSystem/BookingSystems";

import PreConsultPipelineStatus from "./Pipeline-Status/PreConsultPipelineStatus";
import PostConsultPipelineStatus from "./Pipeline-Status/PostConsultPipelineStatus";
import LeadSources from "./Lead-Features/LeadSources";

const tabConfig = [
  {
    key: "leads",
    label: "Leads",
    icon: <PersonOutlineIcon sx={{ fontSize: 20 }} />,
    subTabs: [
      { key: "lead_features", label: "Features" },
      { key: "lead_sources", label: "Lead Sources" },
    ],
  },
  {
    key: "pipeline",
    label: "Pipeline",
    icon: <ViewKanbanOutlinedIcon sx={{ fontSize: 20 }} />,
    subTabs: [
      { key: "pipeline_features", label: "Features" },
    ],
  },
  {
    key: "system",
    label: "System",
    icon: <SettingsOutlinedIcon sx={{ fontSize: 20 }} />,
    subTabs: [
      { key: "services", label: "Services" },
      { key: "theme", label: "Theme" },
      { key: "integrations", label: "Integrations" },
      { key: "sla", label: "SLA" },
    ],
  },
];

export default function Settings() {
  const settings = useSettings();
  const { tab } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const active = tab || "lead_features";

  // Find which main tab the current sub-tab belongs to
  const activeMainTab = useMemo(() => {
    for (const t of tabConfig) {
      if (t.subTabs.some((s) => s.key === active)) return t.key;
    }
    return "leads";
  }, [active]);

  const currentMainTab = tabConfig.find((t) => t.key === activeMainTab);

  const isReady =
    settings &&
    (settings.grade_status.length ||
      settings.pipeline_status.length ||
      settings.lead_features.features.length);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          bgcolor: "#fff",
        }}
      >
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b" }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
          disableElevation
          sx={{
            textTransform: "none",
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": { bgcolor: theme.palette.primary.dark },
          }}
        >
          Save Changes
        </Button>
      </Box>

      {/* Main Tabs */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        <Tabs
          value={activeMainTab}
          onChange={(_, newVal) => {
            const mainTab = tabConfig.find((t) => t.key === newVal);
            if (mainTab) navigate(`/settings/${mainTab.subTabs[0].key}`);
          }}
          sx={{
            px: 3,
            minHeight: 48,
            "& .MuiTabs-indicator": {
              bgcolor: theme.palette.primary.main,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          {tabConfig.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              icon={t.icon}
              iconPosition="start"
              label={t.label}
              sx={{
                textTransform: "none",
                fontWeight: activeMainTab === t.key ? 600 : 400,
                fontSize: "0.875rem",
                color: activeMainTab === t.key ? theme.palette.primary.main : "#64748b",
                minHeight: 48,
                gap: 0.75,
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Sub Tabs */}
      {currentMainTab && (
        <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0" }}>
          <Tabs
            value={active}
            onChange={(_, newVal) => navigate(`/settings/${newVal}`)}
            sx={{
              px: 3,
              minHeight: 40,
              "& .MuiTabs-indicator": {
                bgcolor: theme.palette.primary.main,
                height: 2,
                borderRadius: "2px 2px 0 0",
              },
            }}
          >
            {currentMainTab.subTabs.map((s) => (
              <Tab
                key={s.key}
                value={s.key}
                label={s.label}
                sx={{
                  textTransform: "none",
                  fontWeight: active === s.key ? 600 : 400,
                  fontSize: "0.8125rem",
                  color: active === s.key ? theme.palette.primary.main : "#64748b",
                  minHeight: 40,
                  "&.Mui-selected": {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {!isReady ? (
          <Stack spacing={3}>
            <SettingsSkeleton />
            <SettingsSkeleton />
          </Stack>
        ) : (
          <>
            {active === "lead_grade" && (
              <GradeStatus data={settings.grade_status} />
            )}
            {active === "lead_features" && (
              <LeadFeatures data={settings.lead_features} />
            )}
            {active === "lead_sources" && (
              <LeadSources data={settings.lead_features} />
            )}
            {active === "pipeline_status" && (
              <PipelineStatus data={settings.pipeline_status} />
            )}
            {active === "pre_consult_pipeline" && (
              <PreConsultPipelineStatus data={settings.pipeline_status} />
            )}
            {active === "post_consult_pipeline" && (
              <PostConsultPipelineStatus data={settings.pipeline_status} />
            )}
            {active === "pipeline_features" && (
              <PipelineFeatures data={settings.pipeline_features} />
            )}
            {active === "booking_systems" && <BookingSystems />}
            {active === "services" && <Services />}
            {active === "theme" && <SettingsTheme />}
            {active === "integrations" && <SettingsIntegrations />}
            {active === "sla" && <SettingsSLA value={settings.maxSla || 0} />}
          </>
        )}
      </Box>
    </Box>
  );
}
